import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import {
  DataGridPremium,
  gridClasses,
  useGridApiRef,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowClassNameParams,
  type GridSortModel,
  type GridPaginationModel,
  type GridColumnGroupingModel,
  type GridColumnResizeParams,
  type GridRowSelectionModel,
  type GridRowGroupingModel,
  type GridRowId,
  type GridSingleSelectColDef,
  type GridValidRowModel,
  type GridInitialState,
  type DataGridPremiumProps,
} from '@mui/x-data-grid-premium';
import { frFR } from '@mui/x-data-grid-premium/locales';
import useLocalStorage from 'use-local-storage';
import debounce from 'lodash/debounce';
import { AvatarCell, MoneyCell, ProgressCell, RowActionsMenu, StatusChip } from './cells';
import { DataGridV2Toolbar, type ResolvedQuickFilter, type ResolvedTab } from './Toolbar';
import type { DataGridV2Column, DataGridV2Props } from './types';

/* ── column conversion ──────────────────────────────────────── */

// Synthetic field MUI gives the single grouping column (`rowGroupingColumnMode:
// 'single'`, the default). It isn't part of the consumer `columns`, so its width
// is persisted/restored separately through `groupingColDef` (see below).
const GROUPING_COLUMN_FIELD = '__row_group_by_columns_group__';

const isGroupRow = (params: GridRenderCellParams) => params.rowNode?.type === 'group';

// MUI flags a cell that holds an aggregated result (column-menu "Aggregation",
// footer or group rows) through `params.aggregation`.
const isAggregationCell = (params: GridRenderCellParams) => Boolean(params.aggregation);

const rawCellValue = (params: GridRenderCellParams): ReactNode => {
  const value = params.formattedValue ?? params.value;
  return value === null || value === undefined ? '' : (value as ReactNode);
};

const guardGroup = (params: GridRenderCellParams, render: () => ReactNode): ReactNode => {
  // Group / footer / aggregation rows carry a *partial* row (no original field
  // values), so the descriptor builders (avatar/chip/money/…) crash on them with
  // errors such as "Cannot read properties of undefined (reading 'length' /
  // 'split')". Show the grouped / aggregated value instead of running the custom
  // renderer on those synthetic rows.
  if (isGroupRow(params)) {
    return params.field === '__row_group_by_columns_group__' ? params.value : rawCellValue(params);
  }
  // The aggregation grand-total row is a synthetic `footer` / `pinnedRow` node
  // that carries no leaf fields, and its cells aren't always flagged as
  // aggregation cells — guard the node type too, not just `params.aggregation`.
  const nodeType = params.rowNode?.type;
  if (nodeType === 'footer' || nodeType === 'pinnedRow' || isAggregationCell(params)) {
    return rawCellValue(params);
  }
  return render();
};

// `valueGetter` is invoked for every row, including the synthetic group / footer
// rows produced by row grouping & aggregation. Those rows don't carry the leaf
// fields the consumer accessor reads, so calling it there can throw and tear the
// whole grid down ("Cannot read properties of undefined …"). Run the accessor
// only on genuine leaf rows; skip (no value) the synthetic ones.
type LeafApiRef = {
  current: {
    getRowId?: (row: GridValidRowModel) => GridRowId;
    getRowNode?: (id: GridRowId) => { type?: string } | null;
  } | null;
};

function leafValue<T extends GridValidRowModel, V>(
  row: T,
  apiRef: LeafApiRef | undefined,
  compute: (row: T) => V,
): V | undefined {
  let nodeType: string | undefined;
  try {
    const id = apiRef?.current?.getRowId?.(row);
    if (id !== undefined && id !== null) nodeType = apiRef?.current?.getRowNode?.(id)?.type;
  } catch {
    nodeType = undefined;
  }
  // confident leaf → run unguarded so genuine consumer errors still surface
  if (nodeType === 'leaf') return compute(row);
  // confident group / footer / pinned → no value for this synthetic cell
  if (nodeType !== undefined) return undefined;
  // couldn't resolve the node (likely synthetic): stay defensive
  try {
    return compute(row);
  } catch {
    return undefined;
  }
}

function buildColumn<T extends GridValidRowModel>(
  col: DataGridV2Column<T>,
  widths: Record<string, number>,
  derivedValueOptions?: GridSingleSelectColDef['valueOptions'],
): GridColDef {
  const variant = col.variant ?? (col.render ? 'custom' : 'text');
  // a column that declares (or, for chips, auto-derives) `valueOptions` is
  // filtered/edited through a dropdown holding exactly those items (MUI
  // "singleSelect"), instead of a free-text filter.
  const valueOptions = col.valueOptions ?? derivedValueOptions;

  const base: GridColDef = {
    field: col.field,
    headerName: typeof col.header === 'string' ? col.header : col.field,
    width: widths[col.field] ?? col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    align: col.align ?? (variant === 'money' ? 'right' : undefined),
    headerAlign: col.headerAlign ?? col.align,
    sortable: col.sortable ?? true,
    filterable: col.filterable ?? true,
    // grouping is offered on every column by default (the `actions` variant
    // opts out below); a column can still force it off with `groupable: false`.
    groupable: col.groupable ?? true,
    hideable: col.hideable ?? true,
    editable: col.editable ?? false,
    type: col.type ?? (valueOptions ? 'singleSelect' : undefined),
    valueOptions,
    renderHeader: () => (
      <HeaderLabel>
        <HeaderMain>{col.header ?? col.field}</HeaderMain>
        {col.subHeader ? <HeaderSub>{col.subHeader}</HeaderSub> : null}
      </HeaderLabel>
    ),
  };

  // value used for sort / filter / export (guarded against synthetic group rows)
  if (col.valueGetter) {
    base.valueGetter = (_value: unknown, row: T, _col, apiRef) =>
      leafValue(row, apiRef as LeafApiRef, (r) => col.valueGetter?.(r));
  } else if (variant === 'money' && col.money) {
    base.valueGetter = (_value: unknown, row: T, _col, apiRef) =>
      leafValue(row, apiRef as LeafApiRef, (r) => col.money?.(r, (r as Record<string, unknown>)[col.field]));
  } else if (variant === 'progress' && col.progress) {
    base.valueGetter = (_value: unknown, row: T, _col, apiRef) =>
      leafValue(row, apiRef as LeafApiRef, (r) => {
        const descriptor = col.progress?.(r, (r as Record<string, unknown>)[col.field]);
        return descriptor && descriptor.max > 0 ? descriptor.value / descriptor.max : 0;
      });
  }

  switch (variant) {
    case 'chip':
      base.renderCell = (params) =>
        guardGroup(params, () => {
          const descriptor = col.chip?.(params.row as T, params.value);
          if (!descriptor) return null;
          return (
            <StatusChip tone={descriptor.tone} dot={descriptor.dot}>
              {descriptor.label}
            </StatusChip>
          );
        });
      break;
    case 'avatar':
      base.renderCell = (params) =>
        guardGroup(params, () => {
          const descriptor = col.avatar?.(params.row as T, params.value);
          if (!descriptor) return null;
          return (
            <AvatarCell
              initials={descriptor.initials}
              name={descriptor.name}
              subtitle={descriptor.subtitle}
              tone={descriptor.tone}
            />
          );
        });
      break;
    case 'progress':
      base.renderCell = (params) =>
        guardGroup(params, () => {
          const descriptor = col.progress?.(params.row as T, params.value);
          if (!descriptor) return null;
          return <ProgressCell value={descriptor.value} max={descriptor.max} label={descriptor.label} />;
        });
      break;
    case 'money':
      base.type = col.type ?? 'number';
      base.renderCell = (params) =>
        guardGroup(params, () => {
          const amount = col.money ? col.money(params.row as T, params.value) : (params.value as number);
          return <MoneyCell amount={amount} />;
        });
      break;
    case 'date':
      base.type = 'date';
      base.valueGetter = base.valueGetter
        ? base.valueGetter
        : (_value: unknown, row: T) => {
            const raw = (row as Record<string, unknown>)[col.field];
            return raw ? new Date(raw as string) : undefined;
          };
      break;
    case 'actions':
      base.sortable = false;
      base.filterable = false;
      base.groupable = false;
      base.renderCell = (params) =>
        guardGroup(params, () => {
          const actions = col.actions?.(params.row as T) ?? [];
          return <RowActionsMenu actions={actions} />;
        });
      break;
    case 'custom':
    default:
      if (col.render) {
        base.renderCell = (params) => guardGroup(params, () => col.render?.(params.row as T, params.value));
      }
      break;
  }

  return { ...base, ...col.muiColDef } as GridColDef;
}

/* ── component ──────────────────────────────────────────────── */

const rowText = <T extends GridValidRowModel>(row: T, fields: string[]): string =>
  fields
    .map((field) => {
      const value = (row as Record<string, unknown>)[field];
      return value === null || value === undefined ? '' : String(value);
    })
    .join(' ')
    .toLowerCase();

/**
 * Build `singleSelect` options for a `chip` column out of the distinct values
 * present in the rows, labelling each with its chip descriptor. This turns the
 * column filter into a typed dropdown (e.g. a status select) instead of a
 * free-text search, without the caller having to declare `valueOptions`.
 */
const deriveChipOptions = <T extends GridValidRowModel>(
  col: DataGridV2Column<T>,
  rows: T[],
): GridSingleSelectColDef['valueOptions'] => {
  const seen = new Map<string, { value: unknown; label: string }>();
  for (const row of rows) {
    const value = col.valueGetter ? col.valueGetter(row) : (row as Record<string, unknown>)[col.field];
    if (value === null || value === undefined || value === '') continue;
    const key = String(value);
    if (seen.has(key)) continue;
    const descriptor = col.chip?.(row, value);
    const label = typeof descriptor?.label === 'string' ? descriptor.label : key;
    seen.set(key, { value, label });
  }
  return Array.from(seen.values());
};

function DataGridV2<T extends GridValidRowModel = GridValidRowModel>(props: DataGridV2Props<T>) {
  const {
    rows,
    columns,
    uniqueId,
    getRowId,
    loading,
    loadingVariant = 'overlay',
    search,
    searchPlaceholder,
    searchFields,
    tabs,
    quickFilters,
    quickFilterValues,
    onQuickFilterValuesChange,
    legend,
    toolbarActions,
    enableExport = true,
    enableColumnSelector = true,
    enableFilterPanel = true,
    checkboxSelection,
    onSelectionChange,
    bulkActions,
    pagination = true,
    pageSize,
    hideFooter,
    sortingMode = 'client',
    filterMode = 'client',
    onServerSearch,
    onSortChange,
    editMode = 'cell',
    onRowUpdate,
    onRowUpdateError,
    rowModesModel,
    onRowModesModelChange,
    onRowEditStop,
    isCellEditable,
    columnGroups,
    pinnedColumns,
    defaultGroupingExpansionDepth,
    rowGrouping = true,
    rowGroupingModel: controlledRowGroupingModel,
    onRowGroupingModelChange,
    getRowTone,
    zebra = true,
    autoRowHeight = true,
    rowHeight = 56,
    onRowClick,
    onRowDoubleClick,
    apiRef: externalApiRef,
    initialState,
    muiProps,
    sx,
  } = props;

  const { t } = useTranslation(['dataGrid']);
  const defaultPageSize = pageSize ?? 50;

  let apiRef = useGridApiRef();
  if (externalApiRef) apiRef = externalApiRef;

  // ── persisted state (per uniqueId) ────────────────────────
  const [columnWidths, setColumnWidths] = useLocalStorage<Record<string, number>>(`datagridv2-${uniqueId}-widths`, {});
  const [sortModel, setSortModel] = useLocalStorage<GridSortModel | undefined>(
    `datagridv2-${uniqueId}-sort`,
    undefined,
  );
  const [paginationModel, setPaginationModel] = useLocalStorage<GridPaginationModel>(
    `datagridv2-${uniqueId}-pagination`,
    {
      page: 0,
      pageSize: defaultPageSize,
    },
  );
  const [persistedRowGroupingModel, setPersistedRowGroupingModel] = useLocalStorage<GridRowGroupingModel>(
    `datagridv2-${uniqueId}-grouping`,
    [],
  );
  const [columnOrder, setColumnOrder] = useLocalStorage<string[] | undefined>(
    `datagridv2-${uniqueId}-order`,
    undefined,
  );
  const isRowGroupingControlled = controlledRowGroupingModel !== undefined;
  const rowGroupingModel = rowGrouping
    ? isRowGroupingControlled
      ? controlledRowGroupingModel
      : persistedRowGroupingModel
    : [];

  // ── toolbar state ─────────────────────────────────────────
  const [activeTabKey, setActiveTabKey] = useState<string | undefined>(tabs?.[0]?.key);
  const [searchValue, setSearchValue] = useState('');
  const [internalFilterValues, setInternalFilterValues] = useState<Record<string, string>>({});
  const isQuickFilterControlled = quickFilterValues !== undefined;
  const filterValues = isQuickFilterControlled ? quickFilterValues : internalFilterValues;
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });

  // must match the id type the grid stores in its selection model: when a
  // `getRowId` is provided the grid uses its (string) output, otherwise it keeps
  // the row's native `id` (often a number). Forcing String() here would make
  // `ids.has(...)` always miss and leave the bulk selection empty.
  const resolveRowId = useCallback(
    (row: T): GridRowId => (getRowId ? getRowId(row) : ((row as Record<string, unknown>).id as GridRowId)),
    [getRowId],
  );

  // ── column conversion ─────────────────────────────────────
  // typed filters: auto-derive `singleSelect` options for chip columns that
  // don't declare their own, so e.g. a status column filters through a dropdown.
  const derivedValueOptions = useMemo(() => {
    const map = new Map<string, GridSingleSelectColDef['valueOptions']>();
    for (const col of columns) {
      if (col.variant === 'chip' && !col.valueOptions && (col.filterable ?? true)) {
        map.set(col.field, deriveChipOptions(col, rows));
      }
    }
    return map;
  }, [columns, rows]);

  const gridColumns = useMemo(
    () => columns.map((col) => buildColumn(col, columnWidths, derivedValueOptions.get(col.field))),
    [columns, columnWidths, derivedValueOptions],
  );

  const allFields = useMemo(() => columns.map((col) => col.field), [columns]);
  const searchTextGetters = useMemo(() => {
    const map = new Map<string, (row: T) => string>();
    for (const col of columns) {
      if (col.searchText) map.set(col.field, col.searchText);
    }
    return map;
  }, [columns]);

  // ── client-side filtering (tabs + quick filters + search) ──
  const activeTab = useMemo(() => tabs?.find((tab) => tab.key === activeTabKey), [tabs, activeTabKey]);

  const displayRows = useMemo(() => {
    let result = rows;

    const tabPredicate = activeTab?.predicate;
    if (tabPredicate) {
      result = result.filter((row) => tabPredicate(row));
    }

    if (quickFilters?.length) {
      for (const filter of quickFilters) {
        const value = filterValues[filter.field];
        if (!value) continue;
        const predicate =
          filter.predicate ?? ((row: T, v: string) => String((row as Record<string, unknown>)[filter.field]) === v);
        result = result.filter((row) => predicate(row, value));
      }
    }

    if (searchValue && filterMode !== 'server') {
      const needle = searchValue.toLowerCase();
      const fields = searchFields ?? allFields;
      result = result.filter((row) => {
        if (searchTextGetters.size) {
          const custom = Array.from(searchTextGetters.values())
            .map((getter) => getter(row))
            .join(' ')
            .toLowerCase();
          if (custom.includes(needle)) return true;
        }
        return rowText(row, fields).includes(needle);
      });
    }

    return result;
  }, [
    rows,
    activeTab,
    quickFilters,
    filterValues,
    searchValue,
    filterMode,
    searchFields,
    allFields,
    searchTextGetters,
  ]);

  // ── server search ─────────────────────────────────────────
  const debouncedServerSearch = useMemo(
    () => debounce((value: string) => onServerSearch?.(value), 300),
    [onServerSearch],
  );
  useEffect(() => {
    if (filterMode === 'server') debouncedServerSearch(searchValue);
    return () => debouncedServerSearch.cancel();
  }, [searchValue, filterMode, debouncedServerSearch]);

  // ── resolved tabs (auto counts) ───────────────────────────
  const resolvedTabs: ResolvedTab[] | undefined = useMemo(
    () =>
      tabs?.map((tab) => {
        const predicate = tab.predicate;
        return {
          key: tab.key,
          label: tab.label,
          icon: tab.icon,
          count: tab.count ?? (predicate ? rows.filter((row) => predicate(row)).length : rows.length),
        };
      }),
    [tabs, rows],
  );

  const resolvedQuickFilters: ResolvedQuickFilter[] | undefined = useMemo(
    () =>
      quickFilters?.map(({ field, label, options, display, allLabel }) => ({
        field,
        label,
        options,
        display,
        allLabel,
      })),
    [quickFilters],
  );

  // ── selection ─────────────────────────────────────────────
  const selectedRows = useMemo(() => {
    if (!checkboxSelection) return [];
    const ids = selectionModel.ids;
    if (selectionModel.type === 'exclude') {
      return displayRows.filter((row) => !ids.has(resolveRowId(row)));
    }
    return displayRows.filter((row) => ids.has(resolveRowId(row)));
  }, [checkboxSelection, selectionModel, displayRows, resolveRowId]);

  useEffect(() => {
    onSelectionChange?.(selectedRows);
  }, [selectedRows, onSelectionChange]);

  const clearSelection = useCallback(() => setSelectionModel({ type: 'include', ids: new Set() }), []);

  // ── handlers ──────────────────────────────────────────────
  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      onSortChange?.(model[0]?.field ?? '', model[0]?.sort ?? null);
    },
    [setSortModel, onSortChange],
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => setPaginationModel(model),
    [setPaginationModel],
  );

  const handleRowGroupingModelChange = useCallback(
    (model: GridRowGroupingModel) => {
      if (!isRowGroupingControlled) setPersistedRowGroupingModel(model);
      onRowGroupingModelChange?.(model);
    },
    [isRowGroupingControlled, setPersistedRowGroupingModel, onRowGroupingModelChange],
  );

  const handleColumnResize = useMemo(
    () =>
      debounce((params: GridColumnResizeParams) => {
        setColumnWidths((previous) => ({ ...previous, [params.colDef.field]: params.width }));
      }, 200),
    [setColumnWidths],
  );

  // The grouping column is generated by MUI and isn't part of `gridColumns`, so
  // its persisted width (stored under `GROUPING_COLUMN_FIELD` by the resize
  // handler above) has to be re-applied through `groupingColDef` — otherwise a
  // resized grouped column snaps back to its default width on the next render.
  const groupingColDef = useMemo<DataGridPremiumProps['groupingColDef']>(() => {
    const width = columnWidths[GROUPING_COLUMN_FIELD];
    return width ? { width } : undefined;
  }, [columnWidths]);

  // Persist column order (drag-to-reorder) per `uniqueId`. Read the live order
  // from the grid and drop MUI's synthetic columns (grouping, checkbox…) so only
  // real fields are stored.
  const handleColumnOrderChange = useCallback(() => {
    const fields = apiRef.current
      ?.getAllColumns?.()
      .map((column) => column.field)
      .filter((field) => !field.startsWith('__'));
    if (fields?.length) setColumnOrder(fields);
  }, [apiRef, setColumnOrder]);

  const handleQuickFilterChange = useCallback(
    (field: string, value: string) => {
      if (isQuickFilterControlled) {
        onQuickFilterValuesChange?.({ ...quickFilterValues, [field]: value });
        return;
      }
      setInternalFilterValues((previous) => ({ ...previous, [field]: value }));
    },
    [isQuickFilterControlled, onQuickFilterValuesChange, quickFilterValues],
  );

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<T>) => {
      const classes: string[] = [];
      if (zebra) classes.push(params.indexRelativeToCurrentPage % 2 === 0 ? 'row-even' : 'row-odd');
      const tone = getRowTone?.(params.row);
      if (tone) classes.push(`tone-${tone}`);
      return classes.join(' ');
    },
    [zebra, getRowTone],
  );

  // ── grouping ──────────────────────────────────────────────
  const columnGroupingModel: GridColumnGroupingModel | undefined = useMemo(
    () =>
      columnGroups?.map((group) => ({
        groupId: group.groupId,
        headerName: group.headerName ?? group.groupId,
        children: group.children.map((field) => ({ field })),
      })),
    [columnGroups],
  );

  // Restore the persisted column order at mount by seeding the grid's
  // `initialState` (merged with any consumer-provided one). Fields missing from
  // the saved order are appended by MUI, new columns simply aren't listed.
  // Computed once: the grid only reads `initialState` at mount, and later order
  // changes are persisted through the handler below — not re-seeded here.
  const [mergedInitialState] = useState<GridInitialState | undefined>(() => {
    if (!columnOrder?.length) return initialState;
    return {
      ...initialState,
      columns: {
        ...initialState?.columns,
        orderedFields: columnOrder,
      },
    };
  });

  const processRowUpdate = useMemo(
    () =>
      onRowUpdate
        ? async (newRow: T, oldRow: T): Promise<T> => {
            return onRowUpdate(newRow, oldRow);
          }
        : undefined,
    [onRowUpdate],
  );

  const showChrome = Boolean(
    (resolvedTabs && resolvedTabs.length) ||
      search ||
      (resolvedQuickFilters && resolvedQuickFilters.length) ||
      (legend && legend.length) ||
      enableExport ||
      enableColumnSelector ||
      enableFilterPanel ||
      toolbarActions,
  );

  const toolbarProps = {
    search,
    searchPlaceholder,
    searchValue,
    onSearchChange: setSearchValue,
    tabs: resolvedTabs,
    activeTabKey,
    onTabChange: setActiveTabKey,
    quickFilters: resolvedQuickFilters,
    filterValues,
    onFilterChange: handleQuickFilterChange,
    enableExport,
    enableColumnSelector,
    enableFilterPanel,
    toolbarActions,
    legend,
  };

  const bulkBar =
    checkboxSelection && bulkActions && selectedRows.length > 0 ? (
      <FloatingBulkBar>
        <BulkCount>{t('selected', { count: selectedRows.length })}</BulkCount>
        <BulkActionsSlot>{bulkActions(selectedRows, clearSelection)}</BulkActionsSlot>
        <BulkClearButton type="button" aria-label={t('deselect')} onClick={clearSelection}>
          ✕
        </BulkClearButton>
      </FloatingBulkBar>
    ) : null;

  return (
    <>
      <Card sx={{ width: '100%' }}>
        {loadingVariant === 'bar' && loading ? (
          <LinearProgress sx={{ height: 3 }} data-testid="datagridv2-loading-bar" />
        ) : null}
        <StyledDataGrid
          apiRef={apiRef}
          rows={displayRows}
          columns={gridColumns}
          getRowId={getRowId}
          loading={loadingVariant === 'bar' ? false : loading}
          getRowHeight={autoRowHeight ? () => 'auto' : undefined}
          rowHeight={autoRowHeight ? undefined : rowHeight}
          sx={sx}
          // chrome
          showToolbar={showChrome}
          slots={showChrome ? ({ toolbar: DataGridV2Toolbar } as never) : undefined}
          slotProps={showChrome ? ({ toolbar: toolbarProps } as never) : undefined}
          localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
          // sorting / filtering
          sortingMode={sortingMode}
          filterMode={filterMode}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          // pagination
          pagination={pagination}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[defaultPageSize, defaultPageSize * 2, defaultPageSize * 3]}
          hideFooter={hideFooter}
          // selection
          checkboxSelection={checkboxSelection}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(model) => setSelectionModel(model)}
          disableRowSelectionOnClick
          // editing
          editMode={editMode}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={onRowUpdateError}
          rowModesModel={rowModesModel}
          onRowModesModelChange={onRowModesModelChange}
          onRowEditStop={onRowEditStop}
          isCellEditable={isCellEditable}
          // grouping / pinning
          columnGroupingModel={columnGroupingModel}
          groupingColDef={groupingColDef}
          pinnedColumns={pinnedColumns}
          defaultGroupingExpansionDepth={defaultGroupingExpansionDepth}
          disableRowGrouping={!rowGrouping}
          rowGroupingModel={rowGroupingModel}
          onRowGroupingModelChange={handleRowGroupingModelChange}
          // rows
          getRowClassName={getRowClassName}
          onColumnResize={handleColumnResize}
          onColumnOrderChange={handleColumnOrderChange}
          onRowClick={onRowClick ? (params) => onRowClick(params.row as T) : undefined}
          onCellDoubleClick={onRowDoubleClick ? (params) => onRowDoubleClick(params.row as T) : undefined}
          initialState={mergedInitialState}
          {...muiProps}
        />
      </Card>
      {bulkBar}
    </>
  );
}

export default DataGridV2;
export { DataGridV2 };

/* ── styles ─────────────────────────────────────────────────── */

const StyledDataGrid = styled(DataGridPremium)(() => ({
  border: 'none',
  fontFamily: 'inherit',
  [`& .${gridClasses.columnHeader}`]: {
    backgroundColor: '#F9F9FB',
  },
  [`& .${gridClasses.columnHeaderTitleContainer}`]: {
    padding: 0,
  },
  [`& .${gridClasses.cell}`]: {
    fontSize: 13,
    borderColor: '#F2F2F5',
    display: 'flex',
    alignItems: 'center',
    // give multi-line cells (avatar, status + amount, client + mission…) room to
    // breathe so their content stays vertically centred instead of overflowing
    // into the neighbouring rows.
    paddingTop: 8,
    paddingBottom: 8,
    lineHeight: 1.4,
  },
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: 'none',
  },
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
    outline: 'none',
  },
  [`& .${gridClasses.row}.row-even`]: {
    backgroundColor: '#FCFCFD',
  },
  [`& .${gridClasses.row}.tone-warning`]: {
    backgroundColor: 'rgba(193,122,0,0.05)',
  },
  [`& .${gridClasses.row}.tone-danger`]: {
    backgroundColor: 'rgba(255,51,40,0.025)',
  },
  [`& .${gridClasses.row}.tone-success`]: {
    backgroundColor: 'rgba(77,114,54,0.04)',
  },
  [`& .${gridClasses.row}.Mui-selected`]: {
    backgroundColor: 'rgba(31,102,235,0.045)',
  },
  [`& .${gridClasses.row}:hover`]: {
    backgroundColor: '#F4F6FB',
  },
  [`& .${gridClasses.row}.Mui-selected:hover`]: {
    backgroundColor: 'rgba(31,102,235,0.075)',
  },
  [`& .${gridClasses.footerContainer}`]: {
    borderTop: '1px solid #E4E4EA',
  },
  [`& .${gridClasses.columnSeparator}`]: {
    color: '#E4E4EA',
  },
  [`& .${gridClasses.toolbarContainer}`]: {
    padding: 0,
  },
})) as typeof DataGridPremium;

const Card = styled(Box)(() => ({
  background: '#fff',
  border: '1px solid #E4E4EA',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 2px 16px rgba(33,33,33,.05)',
}));

/**
 * Bulk-action bar. Pinned to the bottom of the viewport so it stays visible
 * whatever the scroll position — the in-grid toolbar scrolls away on long,
 * auto-height grids, which used to hide the bulk actions.
 */
const FloatingBulkBar = styled('div')(() => ({
  position: 'fixed',
  bottom: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1300,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 10px 8px 16px',
  background: '#fff',
  border: '1px solid #DCE6FC',
  borderRadius: 12,
  boxShadow: '0 10px 30px rgba(20,73,184,.18)',
}));

const BulkCount = styled('span')(() => ({
  fontSize: 12.5,
  fontWeight: 700,
  color: '#1449B8',
  whiteSpace: 'nowrap',
}));

const BulkActionsSlot = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}));

const BulkClearButton = styled('button')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  border: 'none',
  borderRadius: 8,
  background: 'transparent',
  color: '#6B6B7E',
  fontSize: 16,
  lineHeight: 1,
  cursor: 'pointer',
  transition: 'background .15s',
  '&:hover': { background: '#F2F2F5', color: '#212121' },
}));

const HeaderLabel = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  lineHeight: 1.2,
}));

const HeaderMain = styled('span')(() => ({
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '.07em',
  textTransform: 'uppercase',
  color: '#6B6B7E',
}));

const HeaderSub = styled('span')(() => ({
  fontSize: 9,
  letterSpacing: '.04em',
  color: '#9898A8',
  marginTop: 1,
}));
